import {
  type FemaSourceContextBinding,
  type FemaSourceContextKind,
  type RefdataBboxResult,
  type RefdataContainsResult,
  type RefdataLatLng,
  type RefdataResultMeta,
} from '../../types/refdata';
import {
  isFemaSourceContextBinding,
  isFemaSourceContextFeature,
  sameFemaSourceContextBinding,
  type FemaSourceContextFeature,
} from '../refdata/femaContextContract';
import { refdataBbox, refdataContains, tryRefdata } from '../refdata/refdataClient';

export interface FemaSourceContextAssessment {
  status: 'verified' | 'partial' | 'unavailable';
  /** Query integrity only. Never a complete mapping, absence or usable-area verdict. */
  canAssessCoverage: boolean;
  reason: string;
  binding?: FemaSourceContextBinding;
  queryKind: 'point' | 'polygon';
  rejectedFeatureCount: number;
  byKind: Record<FemaSourceContextKind, FemaSourceContextFeature[]>;
}

function emptyAssessment(queryKind: FemaSourceContextAssessment['queryKind'], reason: string): FemaSourceContextAssessment {
  return {
    status: 'unavailable', canAssessCoverage: false, queryKind, reason, rejectedFeatureCount: 0,
    byKind: { 'native-mapped': [], 'native-unresolved': [], 'native-unavailable': [],
      'qualified-legacy-sfha': [], 'legacy-unavailable': [], 'source-conflict': [], 'firm-panel': [], 'political-area': [], lomr: [] },
  };
}

/**
 * Bind companion evidence before assessing geography. Keep exact-version known
 * positive legacy rows on partial queries; never turn unavailable/unresolved
 * masks into hazards or let mapped polygons cancel overlapping uncertainty.
 * Primary hazard hits remain owned by the caller, including when this fails.
 */
export function assessFemaSourceContext(
  hazards: RefdataResultMeta | null,
  result: RefdataBboxResult | RefdataContainsResult | null,
  queryKind: FemaSourceContextAssessment['queryKind'],
): FemaSourceContextAssessment {
  const expected = hazards?.femaContext;
  if (hazards?.datasetId !== 'fema-nfhl' || !isFemaSourceContextBinding(expected) || expected.version !== hazards.version) {
    return emptyAssessment(queryKind, 'Flood source has no valid exact-version FEMA context binding; negative coverage is unresolved.');
  }
  if (!result || result.datasetId !== expected.datasetId || result.version !== expected.version
    || !isFemaSourceContextBinding(result.femaContext) || !sameFemaSourceContextBinding(expected, result.femaContext)) {
    return emptyAssessment(queryKind, 'Matching FEMA context data and immutable source receipts are unavailable.');
  }
  const assessed = emptyAssessment(queryKind, 'Exact-version FEMA context query is complete; source footprints still require spatial assessment.');
  assessed.binding = expected;
  if (!Array.isArray(result.features)) return { ...assessed, reason: 'FEMA context query returned malformed source data.' };
  for (const feature of result.features) {
    if (isFemaSourceContextFeature(feature, expected)) assessed.byKind[feature.props.contextKind].push(feature);
    else assessed.rejectedFeatureCount++;
  }
  if (result.truncated !== false || assessed.rejectedFeatureCount > 0) {
    assessed.status = 'partial';
    assessed.reason = assessed.rejectedFeatureCount > 0
      ? 'FEMA context contains malformed or unbound source rows; valid positive evidence is retained, but negative coverage is unresolved.'
      : 'FEMA context query is truncated or lacks a completeness receipt; valid positive evidence is retained, but negative coverage is unresolved.';
    return assessed;
  }
  assessed.status = 'verified';
  assessed.canAssessCoverage = true;
  return assessed;
}

export async function femaSourceContext(
  hazards: RefdataResultMeta | null,
  target: { point: RefdataLatLng; polygon?: readonly RefdataLatLng[] },
): Promise<FemaSourceContextAssessment> {
  const queryKind = target.polygon ? 'polygon' : 'point';
  const expected = hazards?.femaContext;
  if (hazards?.datasetId !== 'fema-nfhl' || !isFemaSourceContextBinding(expected) || expected.version !== hazards.version) {
    return assessFemaSourceContext(hazards, null, queryKind);
  }
  const points = target.polygon ?? [target.point];
  if (points.length < (target.polygon ? 3 : 1) || points.some(p => !Number.isFinite(p.lat) || !Number.isFinite(p.lng)
    || Math.abs(p.lat) > 90 || Math.abs(p.lng) > 180)) {
    return emptyAssessment(queryKind, 'FEMA context query footprint is invalid.');
  }
  const result = await tryRefdata('fema-source-context', async () => {
    if (!target.polygon) return refdataContains('fema-source-context', target.point);
    const lat = points.map(p => p.lat), lng = points.map(p => p.lng);
    // A bbox preserves all intersecting source and context roles. The caller
    // clips them to the selected study area; this box is not a mapped footprint.
    return refdataBbox('fema-source-context', {
      minLat: Math.min(...lat), maxLat: Math.max(...lat),
      minLng: Math.min(...lng), maxLng: Math.max(...lng),
    }, 2000);
  });
  return assessFemaSourceContext(hazards, result, queryKind);
}
